import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Autocomplete, Box, Button, Chip,
  CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider,
  FormControlLabel, MenuItem,
  Paper, Stack, Switch, TextField, ToggleButton, ToggleButtonGroup,
  Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DirectionsWalkIcon from '@mui/icons-material/DirectionsWalk';
import DirectionsBikeIcon from '@mui/icons-material/DirectionsBike';
import TwoWheelerIcon from '@mui/icons-material/TwoWheeler';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import LocalTaxiIcon from '@mui/icons-material/LocalTaxi';
import DirectionsTransitIcon from '@mui/icons-material/DirectionsTransit';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';

import { api } from '../api/client';
import { themeName } from '../copy/mission';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';

import { PlaceAutocomplete } from './PlaceAutocomplete';
import { PlaceChooserDialog } from './PlaceChooserDialog';
import { PlacePicker } from './PlacePicker';
import { TripDayCard, blankDay, type DayDraft } from './TripDayCard';
import {
  countRoadAnswers, DEFAULT_ROAD_OPTIONS, JourneyCharacterFields,
  withRoadDefaults,
} from './JourneyCharacterFields';
import { AvoidancePanel, BoostChoice, CategoryChoice } from './AvoidancePanel';
import { FEATURES } from '../config/features';
import { DurationField } from './DurationField';
import { SitesModeSwitch, useSitesMode, type SitesMode } from './SitesModeSwitch';
import { StayRows } from './StayRows';
import { BreakRow, PinChips } from './PlannerForm';
import { samePlaceProblems } from '../util/samePlace';
import {
  StopRecommendations, useRecommendations,
  type MapPreview,
} from './StopRecommendations';
import type { Category, City, CoordStop, PlaceRef, PlanRequest, Poi, RouteStyle, RouteTheme, TravelMode, TripDayRequest, TripRequest } from '../api/types';
import { SELECTABLE_TRAVEL_MODES, MODE_LABELS } from '../api/types';

/**
 * A trip: what every day has in common, and what each one does differently.
 *
 * The whole design rests on one rule — a day states only what differs. A week
 * based in one hotel is therefore a date and a length, while a chain across
 * three cities is three endpoints. Both are this form.
 */

const MODE_ICONS: Record<TravelMode, React.ReactNode> = {
  walk: <DirectionsWalkIcon fontSize="small" />,
  bike: <DirectionsBikeIcon fontSize="small" />,
  motorcycle: <TwoWheelerIcon fontSize="small" />,
  car: <DirectionsCarIcon fontSize="small" />,
  taxi: <LocalTaxiIcon fontSize="small" />,
  truck: <LocalShippingIcon fontSize="small" />,
  transit: <DirectionsTransitIcon fontSize="small" />,
};

const BREAK_CATEGORIES = {
  lunch: ['restaurant', 'fast_food'],
  coffee: ['cafe', 'ice_cream'],
};

function tomorrow(): string {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
}

/**
 * Minutes from the day's departure to a wall-clock time.
 *
 * Breaks are requested as an offset from the start, not as an hour, because
 * that is what the optimiser schedules against. A break before departure
 * clamps to zero rather than going negative, which would ask for lunch
 * yesterday.
 */
function minutesFrom(startTime: string, at: string): number {
  const [sh, sm] = startTime.split(':').map(Number);
  const [ah, am] = at.split(':').map(Number);
  return Math.max(0, (ah! * 60 + am!) - (sh! * 60 + sm!));
}

/**
 * The wall-clock time an offset from departure lands on.
 *
 * The inverse of `minutesFrom`, needed because a request stores breaks as an
 * offset and the form asks for an hour. Wrapped past midnight rather than
 * running to 26:00, which no time field will accept.
 */
function clockAt(startTime: string, offsetMinutes: number): string {
  const [sh = 0, sm = 0] = startTime.split(':').map(Number);
  const total = (((sh * 60 + sm + offsetMinutes) % 1440) + 1440) % 1440;
  const pad = (n: number) => String(n).padStart(2, '0');

  return `${pad(Math.floor(total / 60))}:${pad(total % 60)}`;
}

/**
 * Whether a day changed a break's timing or place while still inheriting it.
 *
 * Compared against the blanks rather than tracked with a flag: the fields
 * start at the same defaults every day, so anything different is something
 * somebody typed.
 */
function dayBreakEdited(day: DayDraft, which: 'lunch' | 'coffee'): boolean {
  const blank = blankDay(day.date);

  return day[`${which}At`] !== blank[`${which}At`]
    || day[`${which}Minutes`] !== blank[`${which}Minutes`]
    || day[`${which}Place`] !== null
    // Pinning the hour is an edit even when the hour itself is the default:
    // without this, "as the trip, but at 1pm sharp" went out unpinned.
    || Boolean(day[`${which}Pinned`]);
}

function addDays(date: string, days: number): string {
  const [y, m, d] = date.split('-').map(Number);
  const when = new Date(Date.UTC(y!, m! - 1, d!));
  when.setUTCDate(when.getUTCDate() + days);
  return when.toISOString().slice(0, 10);
}

export function TripForm({
  initial,
  city, styles, categories, maxDays, replanSignal = 0,
  planning, onPlan, onShowDetails, colourFor, onSuggestionPreview,
  onCoverageChange,
  base, onBaseChange, finish, onFinishChange, basemapUrl,
  onObjectiveChange, onObjectivePinChange,
  onReturnToBaseChange, mustVisit, onMustVisitChange,
  basePin, finishPin, dayStopPins, dayEndPins, avoidPoints, onDaysChange,
  onBasePinChange, onFinishPinChange, onAvoidPointsChange, onDayStopPinsChange,
  onDayEndPinChange,
  avoid, onAvoidChange, avoidCategories, onAvoidCategoriesChange,
  avoidDistanceM, onAvoidDistanceChange,
  avoidDistanceByPoiId, avoidDistanceByCategoryKey, onAvoidDistanceOverride,
}: {
  /**
   * A trip already described, for the form to open on.
   *
   * The page fills the pickers it owns — the base, the finish, the places —
   * but everything else a trip is made of lives in here: how many days, how
   * they travel, the hours, the breaks, the emphasis. A trip arriving from
   * the assistant showed a planned week above a form still set to three
   * blank days by car, so the first edit would have re-planned it as
   * something nobody asked for.
   */
  initial?: TripRequest | null;
  city: City;
  styles: RouteStyle[];
  categories: Category[];
  maxDays: number;
  /** Raised by the page to ask for a re-plan; see the effect that watches it. */
  replanSignal?: number;
  planning: boolean;
  onPlan: (request: TripRequest) => void;
  /*
   * Where the trip is based, and what it must include — held by the page so
   * the map can draw them before anything is planned, exactly as the
   * single-day screen does with its start, destination and stops.
   */
  base: Poi | null;
  onBaseChange: (poi: Poi | null) => void;
  /*
   * Where each day is meant to finish, when that is somewhere other than the
   * base. Optional — without it every day returns to base, which is what a
   * trip run out of one hotel wants and what this screen assumed until now.
   */
  finish: Poi | null;
  onFinishChange: (poi: Poi | null) => void;
  /**
   * Where the days actually finish, once "return to the base" has had its say.
   *
   * `finish` alone does not answer that: the place chosen is KEPT when the
   * toggle goes back on, so a change of mind costs nothing — which means the
   * field can hold a destination that is not in force. The map needs the
   * answer, not the raw field.
   */
  onObjectiveChange?: (poi: Poi | null) => void;
  /** The tiles the page draws, handed to the place browser's own map. */
  basemapUrl?: string | null;
  /** The same answer for a finish marked on the map rather than chosen. */
  onObjectivePinChange?: (pin: CoordStop | null) => void;
  /**
   * Whether the days come home, so the page knows when both ends are settled.
   *
   * With it on, the base IS the finish; with it off, a destination has to be
   * given before the operation has two ends — and until it has, there is
   * nothing for a stop to sit between.
   */
  onReturnToBaseChange?: (on: boolean) => void;
  /**
   * Spots marked on the map, which have no place record behind them.
   *
   * Held by the page — the map is there — and folded into the request here,
   * because this is where a request is built. A marked base beats a chosen
   * one, since marking is the later instruction.
   */
  basePin?: CoordStop | null;
  finishPin?: CoordStop | null;
  /** Marked stops, by day index, as the request sends them. */
  dayStopPins?: Record<number, CoordStop[]>;
  /** A day's own start and finish, marked on the map rather than chosen. */
  dayEndPins?: Record<number, { start?: CoordStop; end?: CoordStop }>;
  /** Areas marked on the map for the whole operation to keep clear of. */
  avoidPoints?: CoordStop[];
  /*
    Clearing what was marked, from the form.

    A mark made on the map has to be visible and undoable HERE as well, or
    the form quietly disagrees with the map: the base reads empty while every
    day departs from a spot nobody can see named.
  */
  onBasePinChange?: (pin: CoordStop | null) => void;
  onFinishPinChange?: (pin: CoordStop | null) => void;
  onAvoidPointsChange?: (points: CoordStop[]) => void;
  onDayStopPinsChange?: (dayIndex: number, points: CoordStop[]) => void;
  onDayEndPinChange?: (
    dayIndex: number, role: 'start' | 'end', pin: CoordStop | null,
  ) => void;
  /**
   * The days as they stand, so the page can show its day strip before
   * anything is planned — which is what makes the map usable for choosing
   * stops in the first place.
   */
  onDaysChange?: (days: {
    date: string;
    skip: boolean;
    /*
      This day's OWN start and finish, whichever way they were given — the
      place picked in its card, or the spot marked on its map. Reported as
      coordinates because the map only needs somewhere to draw, and sending
      the resolved answer means a change made in the form reaches the map by
      the same path a change made on the map does.
    */
    start: CoordStop | null;
    end: CoordStop | null;
    /** Everything that would change this day's plan, as one value. */
    signature: string;
  }[]) => void;
  mustVisit: Poi[];
  onMustVisitChange: (pois: Poi[]) => void;
  /** Opens a place's full record, as the single-day screen does. */
  onShowDetails: (poiId: string, role?: 'visit' | 'lunch' | 'coffee') => void;
  /** Colour per category, shared with the map so the two always agree. */
  colourFor: (categoryKey: string) => string;
  /**
   * Reports the search corridor for the map to draw, or null to draw none.
   * Omit the prop and the toggle is not offered at all.
   */
  onCoverageChange?: (area: unknown | null) => void;
  /**
   * What any of the suggestion panels wants drawn on the map — this one, or
   * any day's. Only one is ever being read at a time, so the last to report
   * wins, which is what anybody would expect.
   */
  onSuggestionPreview?: (preview: MapPreview) => void;
  /*
   * Avoidance is lifted to the page, as it is on the single-day screen, so the
   * map can draw the exclusion zones before anything is planned.
   */
  avoid: Poi[];
  onAvoidChange: (pois: Poi[]) => void;
  avoidCategories: string[];
  onAvoidCategoriesChange: (keys: string[]) => void;
  avoidDistanceM: number;
  onAvoidDistanceChange: (metres: number) => void;
  /** Per-place and per-category radii, each beating the one below it. */
  avoidDistanceByPoiId: Record<string, number>;
  avoidDistanceByCategoryKey: Record<string, number>;
  onAvoidDistanceOverride: (
    scope: 'poi' | 'category', key: string, metres: number | null,
  ) => void;
}) {
  // ── what every day inherits ──
  const [mode, setMode] = useState<TravelMode>('car');
  const [startTime, setStartTime] = useState('09:00');
  /*
   * How long each day has: six hours to open on, and always a number. The
   * field that sets it is offered only where it does something, exactly as
   * on the single day.
   */
  const [availableMinutes, setAvailableMinutes] = useState(360);
  /*
   * A trip is shaped by a THEME, not a style.
   *
   * They answer the same question and only one of them answers it usefully
   * here: a style feeds the ranking and the optimiser does as it likes, so a
   * week meant to be an infrastructure check could come back as a week of
   * museums. A theme reserves its share of every day's stops before the
   * solve, and carries what to keep clear of with it.
   */
  const [themeKey, setThemeKey] = useState<string | null>(null);
  const [themes, setThemes] = useState<RouteTheme[]>([]);
  useEffect(() => {
    void api.themes().then(setThemes).catch(() => setThemes([]));
  }, []);
  const styleKeys: string[] = [];

  // Off to begin with, as on the single day, where BOTH breaks start off. An
  // operation that quietly books lunch every day has decided something nobody
  // asked for — and the one form it was on was the one that repeats it.
  const [lunch, setLunch] = useState(false);
  const [lunchAt, setLunchAt] = useState('13:00');
  const [lunchMinutes, setLunchMinutes] = useState(45);
  const [lunchPlace, setLunchPlace] = useState<Poi | null>(null);
  // Loose by default, as on the single day: a window nobody chose is the
  // commonest reason a break never appears.
  const [lunchPinned, setLunchPinned] = useState(false);
  const [coffeePinned, setCoffeePinned] = useState(false);
  // Whether each break is found for you or named by you, as on the single day.
  const [breakMode, setBreakMode] = useState<{ lunch: SitesMode; coffee: SitesMode }>({
    lunch: 'kinds', coffee: 'kinds',
  });
  const [coffee, setCoffee] = useState(false);
  const [coffeeAt, setCoffeeAt] = useState('16:00');
  const [coffeeMinutes, setCoffeeMinutes] = useState(20);
  const [coffeePlace, setCoffeePlace] = useState<Poi | null>(null);

  // ── when, and for how long ──
  /* Narrow the pickers, exactly as the single-day form's filters do. */
  /** What every day draws from, and which of those to favour. */
  const [interests, setInterests] = useState<string[]>([]);

  /*
   * An emphasis and "I'll name them" are two answers to one question.
   *
   * `themeKey` rides on the operation's defaults whatever the mode says, so
   * an operation switched to the named mode would still come back with every
   * day full of sites the emphasis chose. The switch asks before taking
   * something the traveller picked off them.
   */
  const [clearingTheme, setClearingTheme] = useState<SitesMode | null>(null);

  // The same split the single day makes, from the same hook.
  const [sitesMode, setSitesMode] = useSitesMode(interests, mustVisit.length);
  // Whether the "name a place yourself" controls are open in the kinds mode.
  const [alsoNaming, setAlsoNaming] = useState(false);
  const showNaming = sitesMode === 'named' || alsoNaming || mustVisit.length > 0;

  /*
   * Whether a daily budget decides anything. Same reasoning as the single
   * day's: it rations the stops BETWEEN base and finish, so with no kinds,
   * no named places and no breaks there is nothing for it to ration.
   */
  // Kinds only, as on the single day: lunch is on by default, so counting a
  // break put this field on an untouched form with nothing to ration.
  const budgetDecides = interests.length > 0;
  const [boostKeys, setBoostKeys] = useState<string[]>([]);
  const [categoryDwell, setCategoryDwell] = useState<Record<string, number>>({});

  /*
   * An emphasis fills the two lists it is mostly made of.
   *
   * The reasoning is the single-day screen's: an emphasis already decides
   * which kinds of site a day draws from and what it keeps clear of, and it
   * applied both on the server while these boxes sat empty — so the one
   * screen that could have shown what the operation would do showed nothing.
   * Filled here, every part of it stays changeable for this trip alone.
   */
  const appliedTheme = useRef<string | null | undefined>(undefined);
  // The lists the emphasis in force contributed, so they can be taken back
  // out again when that emphasis is cleared or replaced.
  const previousLists = useRef<{ draws: string[]; avoids: string[]; boosts: string[] }>(
    { draws: [], avoids: [], boosts: [] },
  );
  // The road answers the emphasis in force contributed, so they can be taken
  // back out again when it is cleared or replaced.
  const previousRoads = useRef<Record<string, number | boolean>>({});
  // The same, for what the journey should be like.
  const previousChars = useRef<Record<string, number>>({});
  useEffect(() => {
    // Filled when the emphasis changes, not on every render: these are the
    // operator's to edit afterwards, and re-applying would undo the edit as
    // fast as it was made.
    if (appliedTheme.current === themeKey) return;
    /*
     * Not until the emphases have arrived.
     *
     * They are fetched, so on a fresh mount this list is empty for a moment —
     * and an emphasis looked up in an empty list answers "no categories, no
     * road preferences, nothing". Marking it applied on that reading recorded
     * that the fill had happened when it had filled nothing, and the guard
     * above then turned away the run that had the real answer.
     *
     * Picking one from the menu was unaffected, because by then the list is
     * loaded. What broke was every path that arrives with an emphasis ALREADY
     * chosen — the form remounted around an assistant's answer, most of all,
     * where the emphasis showed in its box and the two lists it is mostly
     * made of stayed empty beside it.
     */
    if (themeKey && themes.length === 0) return;
    appliedTheme.current = themeKey;

    const chosen = themes.find((t) => t.key === themeKey) ?? null;
    const wasDraws = previousLists.current.draws;
    const wasAvoids = previousLists.current.avoids;
    const wasBoosts = previousLists.current.boosts;
    const nowDraws = chosen?.categoryKeys ?? [];
    const nowAvoids = chosen?.avoidCategoryKeys ?? [];
    const nowBoosts = chosen?.boostCategoryKeys ?? [];
    previousLists.current = { draws: nowDraws, avoids: nowAvoids, boosts: nowBoosts };

    /*
     * Added to rather than replacing, and taken back the same way: only the
     * keys the last emphasis put there are removed, so a category chosen by
     * hand survives both a change of emphasis and clearing it altogether.
     */
    const merge = (current: string[], was: string[], now: string[]) => {
      const kept = current.filter((key) => !was.includes(key));

      return [...new Set([...kept, ...now])];
    };

    if (wasDraws.length > 0 || nowDraws.length > 0) {
      const next = merge(interests, wasDraws, nowDraws);
      setInterests(next);
      /*
       * Minutes are kept only for kinds still drawn from — the same pruning
       * the field itself does when a kind is removed by hand, so an emphasis
       * cannot leave a dwell time behind for a category the trip never visits.
       */
      setCategoryDwell((current) => Object.fromEntries(
        Object.entries(current).filter(([key]) => next.includes(key))));
    }
    if (wasAvoids.length > 0 || nowAvoids.length > 0) {
      onAvoidCategoriesChange(merge(avoidCategories, wasAvoids, nowAvoids));
    }
    /*
     * What it favours among what it draws from — the third list an emphasis
     * carries, and the one that was still being applied invisibly. Taken from
     * the state itself rather than a captured copy, so the fill cannot race
     * the draws set just above it.
     */
    if (wasBoosts.length > 0 || nowBoosts.length > 0) {
      setBoostKeys((current) => merge(current, wasBoosts, nowBoosts));
    }

    /*
     * And the road answers, on the same terms as the lists above.
     *
     * Only values still standing as the last emphasis left them are taken
     * back: one changed by hand has stopped being the emphasis' answer and
     * become the operator's. Merged rather than replacing, because an
     * emphasis silent about ferries has said nothing about them, not "let
     * the router decide".
     */
    const wasRoads = previousRoads.current;
    const nowRoads = {
      ...(chosen?.routingOptions ?? {}),
    } as Record<string, number | boolean>;
    previousRoads.current = nowRoads;

    /*
     * And what it should be like, on exactly the same terms as the roads:
     * only values still standing as the last emphasis left them are taken
     * back, so one moved by hand stays moved.
     */
    const wasChars = previousChars.current;
    const nowChars = { ...(chosen?.routeCharacteristics ?? {}) } as Record<string, number>;
    previousChars.current = nowChars;
    setCharacteristics((current) => {
      const out = { ...current };
      for (const [key, value] of Object.entries(wasChars)) {
        if (out[key] === value) delete out[key];
      }

      return { ...out, ...nowChars };
    });
    setRoadOptions((current) => {
      const out = { ...current };
      for (const [key, value] of Object.entries(wasRoads)) {
        if (out[key] === value) delete out[key];
      }

      /*
        Clearing an emphasis takes back every key it set, which would carry
        Prefer Shortest Route off with it and put the operation back on
        quickest without anybody asking. Put back where nothing says
        otherwise; an explicit off is a value, so it survives.
      */
      return withRoadDefaults({ ...out, ...nowRoads });
    });
    // The lists are read, not watched: re-running on every edit to them would
    // fight the operator for the box they are typing in.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [themeKey, themes]);
  const [avoidFilter, setAvoidFilter] = useState('');

  const [tripStart, setTripStart] = useState(tomorrow());
  /**
   * Stay times per place, for the whole trip.
   *
   * Trip-wide because a place is worth the same hour whichever day it lands
   * on. A day that wants its own says so on the day, and the server merges
   * the day's over these.
   */
  const [stopDwellMinutes, setStopDwellMinutes] = useState<Record<string, number>>({});
  /*
   * Trip-wide preferences, the same two the single-day screen offers.
   *
   * Both already existed on `TripRequest.defaults` and were being sent
   * hard-coded — safety off, tolls unmentioned — so a trip could not ask for
   * what a single day could.
   */
  /*
   * The road answers for the whole trip.
   *
   * Trip-level rather than per-day, like the mode and the tolls switch beside
   * it: "how this one travels" is a property of the operation, not of a
   * Tuesday. Prefilled from the emphasis the same way the single-day form
   * does it, so an emphasis that answers the road questions shows those
   * answers here instead of applying them out of sight.
   */
  const [roadOptions, setRoadOptions] = useState<Record<string, number | boolean>>(
    { ...DEFAULT_ROAD_OPTIONS },
  );
  /*
   * What the journey should be like, for the whole operation.
   *
   * The other half of the road question, and it was missing here entirely
   * while the single-day form has asked it all along — a week could not be
   * asked for a prettier or a quieter road at any price. Trip-level like the
   * road answers beside it: "keep near a hospital" is a property of the
   * operation, not of a Tuesday.
   */
  const [characteristics, setCharacteristics] =
    useState<Record<string, number>>({});
  const [avoidTolls, setAvoidTolls] = useState(false);

  /*
   * What the section's own title says, and how much of it has been answered.
   *
   * The count is the point: shut, the section has to say whether anything
   * inside it is set, or it reads as somewhere nothing has been decided.
   */
  const chosenThemeForRoads = themes.find((t) => t.key === themeKey) ?? null;
  const roadAnswerCount =
    countRoadAnswers(roadOptions) + Object.keys(characteristics).length;

  const [safetyPreference, setSafetyPreference] = useState(false);

  const setStopDwell = useCallback((poiId: string, minutes: number | null) => {
    setStopDwellMinutes((current) => {
      const next = { ...current };
      if (minutes === null) delete next[poiId];
      else next[poiId] = minutes;
      return next;
    });
  }, []);

  /* Only the modes that pay tolls. A day may travel differently; this is the
     trip's default, which is what the preference applies to. */
  const tollsApply = ['car', 'taxi', 'motorcycle', 'truck'].includes(mode);

  const [days, setDays] = useState<DayDraft[]>(
    () => [0, 1, 2].map((i) => blankDay(addDays(tomorrow(), i))),
  );

  /*
   * Dates follow the trip's start date, day by day.
   *
   * Asked once here rather than typed five times, which is the whole reason
   * for a start date — but a day whose date was moved by hand keeps it. A
   * trip with a rest day in the middle is a real thing, and having the
   * sequence silently re-flatten it would be worse than not helping at all.
   */
  useEffect(() => {
    setDays((current) => current.map((day, i) => (
      day.dateTouched ? day : { ...day, date: addDays(tripStart, i) }
    )));
  }, [tripStart]);

  /* Endpoints and places are city-scoped; the page clears the lifted ones. */
  useEffect(() => {
    setLunchPlace(null);
    setCoffeePlace(null);
    setDays((current) => current.map((d) => ({
      ...d, start: null, end: null, mustVisit: [],
    })));
  }, [city.id]);

  const visitable = useMemo(() => categories.filter((c) => c.isVisitable), [categories]);

  /*
   * The one thing a trip cannot be planned without.
   *
   * A route style, or a theme on every day. Interests are deliberately not
   * counted: they narrow what a day offers to suggest, which is a different
   * question from what the day is FOR, and treating them as the trip's
   * direction made an optional field look compulsory.
   *
   * The server accepts interests here too, since a request carrying only
   * those still gives the planner something to work from. This is the
   * stricter of the two on purpose — it is guidance, not validation.
   */
  const hasDirection = Boolean(themeKey)
    // Places to visit are what a day is FOR, as plainly as a style is —
    // whether they were named in the form or marked on the map. A day told
    // where to go does not also need to be told what it is about.
    || mustVisit.length > 0
    || days.every((d, i) => d.skip
      || d.styleKeys.length > 0
      || d.mustVisit.length > 0
      || (dayStopPins?.[i]?.length ?? 0) > 0);
  const active = days.filter((d) => !d.skip);
  /*
    A base chosen from the list OR marked on the map.

    This read the picker alone, so marking the base on the map left the plan
    button dead with nothing on screen explaining why — the one thing the map
    was just given the power to set was the one thing that did not count as
    set. `submit` has always been willing to take either.
  */
  /*
    Enough to plan: somewhere to set off from, and a day to plan.

    `hasDirection` used to be required here too, which made a note that calls
    itself guidance behave as validation — and left somebody who had marked
    a start and a destination on the map staring at a dead button, since
    neither is a style or a theme. A base-to-destination run with no stops is
    a real thing to ask for, the server plans it, and the note below still
    says what it will look like. Pressing it is their call.
  */
  const ready = Boolean(base || basePin) && active.length > 0;


  /*
   * A based operation: every day finishes where it set off from.
   *
   * Off to begin with, so both ends are stated rather than assumed. On by
   * default it answered the second half of the question before anybody had
   * been asked it — and quietly: an operation that already had both ends
   * offered to put stops between them before either had been named.
   *
   * Still one switch away for the common trip run out of one hotel, and the
   * destination it keeps is kept when the switch goes back on.
   */
  const [returnToBase, setReturnToBase] = useState(false);
  /*
   * The objective actually in force. Switching the toggle back on keeps the
   * place that was chosen rather than discarding it, so a change of mind
   * costs nothing — which is why this needs no confirmation, unlike the
   * single-day screen, where the round trip destroys the destination.
   */
  const objective = returnToBase ? null : finish;
  /*
    One place cannot be two things on the operation either.

    The operation's own ends and the places every day is told to hold — a
    day's own lists are checked in its card. `returnToBase` is the
    multi-day spelling of a round trip, and exempts the ends from the rule
    for the same reason: finishing where it began is what that switch means.
  */
  const placeProblems = samePlaceProblems({
    start: base
      ? { lat: base.lat, lng: base.lng, id: base.id, label: base.name }
      : basePin
        ? { lat: basePin.lat, lng: basePin.lng, label: basePin.label ?? 'the marked base' }
        : null,
    end: objective
      ? { lat: objective.lat, lng: objective.lng, id: objective.id, label: objective.name }
      : finishPin
        ? { lat: finishPin.lat, lng: finishPin.lng, label: finishPin.label ?? 'the marked finish' }
        : null,
    stops: mustVisit.map((p) => ({ lat: p.lat, lng: p.lng, id: p.id, label: p.name })),
    roundTrip: returnToBase,
  });

  /*
   * Told to the page, which draws it.
   *
   * The map showed the base and nothing else: choosing a destination put an
   * E on no map at all until the operation had been planned, so the one
   * check anybody makes before planning — "is that the right end?" — could
   * not be made.
   */
  useEffect(() => { onObjectiveChange?.(objective); }, [objective, onObjectiveChange]);

  /*
    Marking where the days finish says they do not come home.

    Without this the toggle stayed on while an E sat on the map — the form
    saying every day returns to the base and the map saying otherwise, which
    is the one disagreement a marked spot must never create. Marking is the
    later instruction, so it wins; the switch is still there to go back.
  */
  useEffect(() => {
    if (finishPin) setReturnToBase(false);
  }, [finishPin]);

  // And what is actually in force, which is what the map draws.
  useEffect(() => {
    onObjectivePinChange?.(returnToBase ? null : finishPin ?? null);
  }, [returnToBase, finishPin, onObjectivePinChange]);

  useEffect(() => { onReturnToBaseChange?.(returnToBase); }, [returnToBase, onReturnToBaseChange]);

  /*
   * A trip already described, read back into the controls that describe it.
   *
   * Keyed on the request's identity rather than its contents: this is an
   * arrival, not a subscription. Re-running on every change would take the
   * form back off whoever was editing it — the request is rewritten by every
   * edit, so the two would fight for the field being typed in.
   *
   * The emphasis is marked applied as it is set. The effect that fills the
   * lists from a theme exists to answer somebody CHOOSING one, and the lists
   * are already here in the request; letting it run would merge the theme's
   * current categories over a trip that was planned with whatever they were
   * at the time.
   */
  const seeded = useRef<TripRequest | null>(null);
  useEffect(() => {
    if (!initial || seeded.current === initial) return;
    seeded.current = initial;
    const { defaults } = initial;

    setMode(defaults.mode ?? 'car');
    const departure = defaults.startTime ?? '09:00';
    setStartTime(departure);
    if (defaults.availableMinutes) setAvailableMinutes(defaults.availableMinutes);
    setInterests(defaults.interestCategoryKeys ?? []);
    setBoostKeys(defaults.boostCategoryKeys ?? []);
    setCategoryDwell(defaults.dwellMinutesByCategoryKey ?? {});
    setStopDwellMinutes(defaults.stopDwellMinutes ?? {});
    setRoadOptions((defaults.routingOptions as Record<string, number | boolean>)
      ?? { ...DEFAULT_ROAD_OPTIONS });
    previousRoads.current = {};
    setCharacteristics((defaults.routeCharacteristics ?? {}) as Record<string, number>);
    previousChars.current = {};
    setAvoidTolls(Boolean(defaults.avoidTolls));
    setSafetyPreference(defaults.safetyPreference === 'prefer');

    /*
     * Whether every day comes back to base is the presence of an end, which
     * is how the request itself says it — and the toggle defaults to "yes",
     * so a trip that finishes somewhere else would have had its objective
     * dropped by the first edit from this form.
     */
    setReturnToBase(!defaults.end);

    for (const kind of ['lunch', 'coffee'] as const) {
      const pause = defaults[kind];
      const setEnabled = kind === 'lunch' ? setLunch : setCoffee;
      const setAt = kind === 'lunch' ? setLunchAt : setCoffeeAt;
      const setMinutes = kind === 'lunch' ? setLunchMinutes : setCoffeeMinutes;
      setEnabled(Boolean(pause));
      if (!pause) continue;
      setAt(clockAt(departure, pause.targetMinutesFromStart ?? 0));
      if (pause.durationMinutes) setMinutes(pause.durationMinutes);
    }

    // The theme rides on the days rather than on the defaults, because the
    // planner reads one per day — so the trip's is whichever the days agree on.
    const themes = new Set(initial.days.map((d) => d.themeKey).filter(Boolean));
    const only = themes.size === 1 ? [...themes][0] ?? null : null;
    appliedTheme.current = only;
    setThemeKey(only);

    const first = initial.days[0]?.date;
    if (first) setTripStart(first);

    /*
     * Places are resolved because the day cards show names. A day out of the
     * assistant carries only a date and a theme, so this usually resolves
     * nothing at all — but a request edited here and planned again carries
     * whatever was added to it, and a form that silently dropped those would
     * lose them on the next plan.
     */
    let cancelled = false;
    const place = async (ref?: PlaceRef | null) => (
      ref && ref.kind === 'poi' ? api.poi(ref.poiId).catch(() => null) : null
    );
    void (async () => {
      const drafts = await Promise.all(initial.days.map(async (day, i) => {
        const [start, end, must] = await Promise.all([
          place(day.start),
          place(day.end),
          Promise.all((day.mustVisitPoiIds ?? []).map(
            (id) => api.poi(id).catch(() => null))),
        ]);
        const blank = blankDay(day.date ?? addDays(first ?? tomorrow(), i));

        return {
          ...blank,
          // Only a date that breaks the run from the first day is "moved by
          // hand"; the rest still follow the trip's start date, which is what
          // that field is for.
          dateTouched: Boolean(day.date && first && day.date !== addDays(first, i)),
          start, end,
          mode: day.mode ?? null,
          styleKeys: day.routeStyleKeys ?? [],
          interests: day.interestCategoryKeys ?? [],
          boostKeys: day.boostCategoryKeys ?? [],
          categoryDwell: day.dwellMinutesByCategoryKey ?? {},
          /*
           * The trip's own must-visits ride on the first planned day, and the
           * page holds them; taking them here as well would show each one
           * twice and send it twice.
           */
          mustVisit: (i === initial.days.findIndex((d) => !d.skip)
            ? [] : must.filter((poi): poi is Poi => poi !== null)),
          availableMinutes: day.availableMinutes ?? null,
          skip: Boolean(day.skip),
          lunch: day.lunch === null ? 'off' as const : blank.lunch,
          coffee: day.coffee === null ? 'off' as const : blank.coffee,
        };
      }));
      if (!cancelled) setDays(drafts);
    })();

    return () => { cancelled = true; };
    // Identity only, for the reason given above: everything else here is a
    // setter or a ref, so there is nothing else to watch.
  }, [initial]);

  /**
   * The request the trip's recommendations are ranked against.
   *
   * A round trip from the base. Null until there is a base, which is exactly
   * when the panel has nothing to say — it hides itself rather than offering
   * to rank a corridor that does not exist yet.
   */
  const draft = useMemo<PlanRequest | null>(() => {
    if (!base) return null;
    const ref = { kind: 'poi' as const, poiId: base.id };
    return {
      cityId: city.id,
      start: ref,
      end: objective ? { kind: 'poi' as const, poiId: objective.id } : ref,
      mode,
      availableMinutes,
      mustVisitPoiIds: [],
      interestCategoryKeys: [],
      ...(styleKeys.length > 0 ? { routeStyleKeys: styleKeys } : {}),
      avoid: { poiIds: avoid.map((p) => p.id), categoryKeys: avoidCategories },
      safetyPreference: 'off',
      /*
       * The breaks ride along so the panel can rank places for them too.
       * Recommendations return empty lists for a break that was not asked
       * for, so this is also what makes the suggestion links below work.
       */
      ...(lunch
        ? {
            lunch: {
              targetMinutesFromStart: minutesFrom(startTime, lunchAt),
              durationMinutes: lunchMinutes,
              flexibilityMinutes: 60,
            },
          }
        : {}),
      ...(coffee
        ? {
            coffee: {
              targetMinutesFromStart: minutesFrom(startTime, coffeeAt),
              durationMinutes: coffeeMinutes,
              flexibilityMinutes: 60,
            },
          }
        : {}),
    };
  }, [
    base, objective, city.id, mode, availableMinutes, styleKeys, avoid, avoidCategories,
    startTime, lunch, lunchAt, lunchMinutes, coffee, coffeeAt, coffeeMinutes,
  ]);

  const recommendations = useRecommendations(draft);

  /*
   * The corridor a day would search, fetched only while somebody is looking.
   *
   * Drawn from the trip's own round trip, which is the first day's corridor
   * for a based trip and a fair approximation of the rest — the same request
   * the suggestions above are ranked against.
   */
  const [showCoverage, setShowCoverage] = useState(false);

  useEffect(() => {
    if (!showCoverage || !draft || !onCoverageChange) {
      onCoverageChange?.(null);
      return;
    }
    let cancelled = false;
    void api.coverage(draft)
      .then((r) => { if (!cancelled) onCoverageChange(r.area); })
      .catch(() => { if (!cancelled) onCoverageChange(null); });
    return () => { cancelled = true; };
  }, [showCoverage, draft, onCoverageChange]);

  useEffect(() => {
    onSuggestionPreview?.(recommendations.preview);
  }, [recommendations.preview, onSuggestionPreview]);

  /*
    Which list the catalogue is open for: the operation's, or one day's.

    Owned here rather than by the page, because these are the lists it
    fills — the operation's base, objective and places to visit, and each
    day's own three. The page holds none of them: the days are drafts in
    this form until the operation is planned.
  */
  const [browsing, setBrowsing] = useState<'trip' | number | null>(null);

  const setDay = (index: number, patch: Partial<DayDraft>) =>
    setDays((current) => current.map((d, i) => (i === index ? { ...d, ...patch } : d)));

  /*
   * Planned from outside, without duplicating what a plan IS.
   *
   * The prompt over the map has to re-send the operation, and only this form
   * knows how one is built — the defaults, every day's overrides, the breaks.
   * Rebuilding that on the page would be the same request assembled twice,
   * drifting apart on the first change to either. A counter is enough: the
   * page raises it, this submits exactly what the button submits.
   *
   * Skipped on the first render, or a form that has only just appeared would
   * plan itself.
   */
  const planSignal = useRef(replanSignal);
  useEffect(() => {
    if (planSignal.current === replanSignal) return;
    planSignal.current = replanSignal;
    submit();
    // `submit` is rebuilt every render and reads current state either way.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [replanSignal]);

  /*
    What the page needs to draw its day strip before anything is planned.

    Reported rather than lifted: the days are edited here, and the page only
    has to know how many there are and when.
  */
  useEffect(() => {
    const at = (poi: Poi | null, pin: CoordStop | null | undefined): CoordStop | null => {
      if (pin) return pin;

      return poi ? { lat: poi.lat, lng: poi.lng, label: poi.name } : null;
    };

    onDaysChange?.(days.map((d, i) => ({
      date: d.date,
      skip: d.skip,
      // This day's OWN ends, where it has them. The page draws these in
      // place of the operation's for the day it is showing — without them a
      // day that finishes somewhere else still drew the operation's E.
      start: at(d.start, dayEndPins?.[i]?.start),
      end: at(d.end, dayEndPins?.[i]?.end),
      /*
        Everything about this day that would change its plan.

        The page keeps the value each day was planned from and marks the day
        out of date when this stops matching. Without it a day edited HERE
        said nothing at all — no toast, no badge — while the same edit made
        on the map re-planned at once and announced itself, so the form
        looked like it had quietly ignored what was typed into it.
      */
      signature: JSON.stringify([
        d.date, d.skip, d.mode, d.styleKeys,
        d.interests, d.boostKeys, d.categoryDwell,
        d.mustVisit.map((x) => x.id), d.stopDwellMinutes, d.availableMinutes,
        d.start?.id ?? null, d.end?.id ?? null,
        d.lunch, d.lunchAt, d.lunchMinutes, d.lunchPlace?.id ?? null,
        d.lunchPinned ?? null, d.lunchMode ?? null,
        d.coffee, d.coffeeAt, d.coffeeMinutes, d.coffeePlace?.id ?? null,
        d.coffeePinned ?? null, d.coffeeMode ?? null,
        d.avoid.map((x) => x.id), d.avoidCategories, d.avoidDistanceM,
        d.avoidDistanceByPoiId, d.avoidDistanceByCategoryKey,
        d.sitesMode ?? null,
        dayEndPins?.[i] ?? null, dayStopPins?.[i] ?? null,
      ]),
    })));
  }, [days, dayEndPins, dayStopPins, onDaysChange]);

  const submit = () => {
    /*
      A base chosen from the list, or one marked on the map — either will do,
      and the mark wins when there is both, because marking it is the later
      instruction.
    */
    const marked = (pt: CoordStop) => (
      { kind: 'coords' as const, lat: pt.lat, lng: pt.lng, ...(pt.label ? { label: pt.label } : {}) }
    );
    const startRef = basePin ? marked(basePin) : base ? { kind: 'poi' as const, poiId: base.id } : null;
    if (!startRef) return;
    const ref = (p: Poi) => ({ kind: 'poi' as const, poiId: p.id });
    const breakFor = (place: Poi | null, target: number, duration: number) => ({
      targetMinutesFromStart: target,
      durationMinutes: duration,
      flexibilityMinutes: 60,
      ...(place ? { poiId: place.id } : {}),
    });

    /*
      One day's break, read the way the one-day form reads its own.

      Both of these were missing here. The hour went out as a hard window
      whatever the card said, so every per-day break was pinned and a day
      that could not reach 1pm simply lost its lunch — while the same
      operation's trip-wide break, built a few lines above, was free to land
      where it fitted. And the named place was sent even after the card was
      switched back to letting the planner choose, so "choose for me" kept
      going to the place last named.
    */
    const dayBreak = (d: DayDraft, which: 'lunch' | 'coffee') => {
      const named = (d[`${which}Mode`] ?? (d[`${which}Place`] ? 'named' : 'kinds')) === 'named';

      return {
        ...breakFor(
          named ? d[`${which}Place`] : null,
          minutesFrom(startTime, d[`${which}At`]),
          d[`${which}Minutes`],
        ),
        ...(d[`${which}Pinned`] ? {} : { anytime: true }),
      };
    };

    onPlan({
      cityId: city.id,
      defaults: {
        start: startRef,
        ...(finishPin
          ? { end: marked(finishPin) }
          : objective ? { end: ref(objective) } : {}),
        mode,
        startTime,
        availableMinutes,
        ...(styleKeys.length > 0 ? { routeStyleKeys: styleKeys } : {}),
        /*
          Kinds travel with the operation only while it is being built FROM
          kinds, exactly as on the single day. Sending them under "I'll name
          them" would fill every day with places nobody picked, beside the
          handful that were. The values are kept, not cleared, so switching
          back sends them again unchanged.

          A DAY that names its own kinds still overrides this; that is the
          day's own switch to make, not the operation's.
        */
        ...(sitesMode === 'kinds' && interests.length > 0
          ? { interestCategoryKeys: interests } : {}),
        ...(Object.keys(categoryDwell).length > 0
          ? { dwellMinutesByCategoryKey: categoryDwell } : {}),
        ...(sitesMode === 'kinds' && boostKeys.filter((k) => interests.includes(k)).length > 0
          ? { boostCategoryKeys: boostKeys.filter((k) => interests.includes(k)) }
          : {}),
        ...(lunch
          ? { lunch: {
              ...breakFor(breakMode.lunch === 'named' ? lunchPlace : null,
                minutesFrom(startTime, lunchAt), lunchMinutes),
              ...(lunchPinned ? {} : { anytime: true }),
            } }
          : {}),
        ...(coffee
          ? { coffee: {
              ...breakFor(breakMode.coffee === 'named' ? coffeePlace : null,
                minutesFrom(startTime, coffeeAt), coffeeMinutes),
              ...(coffeePinned ? {} : { anytime: true }),
            } }
          : {}),
        avoid: {
          poiIds: avoid.map((p) => p.id),
          categoryKeys: avoidCategories,
          // Areas marked on the map, which have no place record to name.
          ...(avoidPoints?.length ? { points: avoidPoints } : {}),
          distanceByPoiId: avoidDistanceByPoiId,
          distanceByCategoryKey: avoidDistanceByCategoryKey,
          ...(avoid.length > 0 || avoidCategories.length > 0 || avoidPoints?.length
            ? { distanceM: avoidDistanceM } : {}),
        },
        safetyPreference: safetyPreference ? 'prefer' : 'off',
        ...(tollsApply && avoidTolls ? { avoidTolls: true } : {}),
        /*
         * Only what was answered. An empty object would say "no preferences"
         * where the truth is that nobody was asked, leaving the emphasis'
         * own answers nothing to merge with.
         */
        ...(Object.keys(roadOptions).length > 0
          ? { routingOptions: roadOptions as PlanRequest['routingOptions'] } : {}),
        // Only what was answered, for the reason given above the roads.
        ...(Object.keys(characteristics).length > 0
          ? {
            routeCharacteristics:
              characteristics as PlanRequest['routeCharacteristics'],
          } : {}),
        ...(Object.keys(stopDwellMinutes).length > 0 ? { stopDwellMinutes } : {}),
      },
      days: days.map((d, i): TripDayRequest => ({
        date: d.date,
        // The trip's theme rides on every day: `defaults` has no themeKey,
        // because the planner reads a theme per day — which is also what
        // lets one day of a week be something else.
        ...(themeKey ? { themeKey } : {}),
        /*
          A spot marked on the map for this day beats the place picked from
          its list: it was said later, and the map is where it was said.

          A day took a PLACE at each end and had nowhere to put a bare
          coordinate, so marking one for a single day was dropped — the toast
          said it was set, nothing stored it, and the map went on drawing the
          operation's ends.
        */
        ...(dayEndPins?.[i]?.start
          ? { start: marked(dayEndPins[i].start!) }
          : d.start ? { start: ref(d.start) } : {}),
        ...(dayEndPins?.[i]?.end
          ? { end: marked(dayEndPins[i].end!) }
          : d.end ? { end: ref(d.end) } : {}),
        ...(d.mode ? { mode: d.mode } : {}),
        ...(d.styleKeys.length > 0 ? { routeStyleKeys: d.styleKeys } : {}),
        /*
          The day's own switch, honoured here for the same reason the
          operation's is above: a mode that changed only what is on screen
          would leave a day the operator had set to "I'll name them" still
          being filled with kinds.
        */
        ...((d.sitesMode ?? 'kinds') === 'kinds' && d.interests.length > 0
          ? { interestCategoryKeys: d.interests } : {}),
        ...((d.sitesMode ?? 'kinds') === 'kinds'
          && d.boostKeys.filter((k) => d.interests.includes(k)).length > 0
          ? { boostCategoryKeys: d.boostKeys.filter((k) => d.interests.includes(k)) }
          : {}),
        ...(Object.keys(d.categoryDwell).length > 0
          ? { dwellMinutesByCategoryKey: d.categoryDwell } : {}),
        /*
         * Places asked for by name, on the trip and on this day. The trip's go
         * on the first planned day: a must-visit is a thing you want to do
         * once, and repeating it every day is not what anybody means.
         */
        ...(Object.keys(d.stopDwellMinutes).length > 0
          ? { stopDwellMinutes: d.stopDwellMinutes } : {}),
        mustVisitPoiIds: [
          ...(i === days.findIndex((x) => !x.skip) ? mustVisit.map((p) => p.id) : []),
          ...d.mustVisit.map((p) => p.id),
        ],
        // Spots marked on this day's map before it was planned.
        ...(dayStopPins?.[i]?.length ? { mustVisitPoints: dayStopPins[i] } : {}),
        ...(d.availableMinutes ? { availableMinutes: d.availableMinutes } : {}),
        /*
         * A break the day set for itself carries its own hour, length and
         * place. `null` declines the trip's; saying nothing inherits it —
         * and a day that inherits but moved the hour states it, since the
         * timing is its own even when the break is not.
         */
        ...(d.lunch === 'off' ? { lunch: null } : {}),
        ...(d.lunch === 'on' || (d.lunch === 'inherit' && lunch && dayBreakEdited(d, 'lunch'))
          ? { lunch: dayBreak(d, 'lunch') }
          : {}),
        ...(d.coffee === 'off' ? { coffee: null } : {}),
        ...(d.coffee === 'on' || (d.coffee === 'inherit' && coffee && dayBreakEdited(d, 'coffee'))
          ? { coffee: dayBreak(d, 'coffee') }
          : {}),
        /* A day's own avoidance replaces the trip's rather than adding to it. */
        ...(d.avoid.length > 0 || d.avoidCategories.length > 0
          ? {
              avoid: {
                poiIds: d.avoid.map((p) => p.id),
                categoryKeys: d.avoidCategories,
                distanceM: d.avoidDistanceM,
                distanceByPoiId: d.avoidDistanceByPoiId,
                distanceByCategoryKey: d.avoidDistanceByCategoryKey,
              },
            }
          : {}),
        ...(d.skip ? { skip: true } : {}),
      })),
    });
  };

  return (
    <Stack spacing={2.5}>
      {/* ── the trip ─────────────────────────────────────────────── */}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.4 }}>
          THE WHOLE OPERATION
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          Every day inherits this. Any day can change any of it below.
        </Typography>

        <Stack spacing={2}>
          <Stack direction="row" spacing={2}>
            <TextField
              size="small" type="date" label="Operation starts"
              value={tripStart}
              onChange={(e) => setTripStart(e.target.value)}
              sx={{ flex: 1 }}
              helperText="Days run on from here"
            />
            <TextField
              size="small" select label="Days"
              value={days.length}
              onChange={(e) => {
                const want = Number(e.target.value);
                setDays((current) => (want <= current.length
                  ? current.slice(0, want)
                  : [
                      ...current,
                      ...Array.from({ length: want - current.length }, (_, k) =>
                        blankDay(addDays(tripStart, current.length + k))),
                    ]));
              }}
              sx={{ width: 110 }}
            >
              {Array.from({ length: maxDays }, (_, i) => i + 1).map((n) => (
                <MenuItem key={n} value={n}>{n}</MenuItem>
              ))}
            </TextField>
          </Stack>

          <PlaceAutocomplete
            cityId={city.id}
            allCities
            label="Base of operations"
            placeholder="Accommodation, depot, address or a site nearby"
            value={base}
            onChange={onBaseChange}
            helperText={basePin
              ? 'The marked spot is used; clear it below to go back to this'
              : 'Every day departs from and returns here unless a day says otherwise'}
          />
          {/* Marked on the map, and beating the picker above — said here so
              the two never silently disagree. */}
          {basePin && onBasePinChange && (
            <PinChips pins={[basePin]} onChange={() => onBasePinChange(null)} colour="primary" />
          )}

          {/*
            On by default, and the objective is hidden while it is: two fields
            that must agree are two fields that can disagree, which is the
            reasoning the single-day screen gives for hiding its destination
            behind the same switch.
          */}
          <FormControlLabel
            control={
              <Switch
                checked={returnToBase}
                onChange={(event) => setReturnToBase(event.target.checked)}
              />
            }
            label="Every day returns to the base"
            sx={{ mt: -1 }}
          />

          {/*
            Named, this is where each day finishes — not merely the last one —
            because that is what the server does with it, and a field reading
            as "the trip ends here" while behaving otherwise is worse than no
            field at all.
          */}
          {!returnToBase && (
            <PlaceAutocomplete
              cityId={city.id}
              allCities
              label="Destination"
              placeholder="Somewhere other than the base"
              value={finish}
              onChange={onFinishChange}
              helperText={finishPin
                ? 'The marked spot is used; clear it below to go back to this'
                : finish
                  ? 'Each day finishes here, and the next sets off from it'
                  : 'Where each day finishes instead of the base'}
            />
          )}
          {!returnToBase && finishPin && onFinishPinChange && (
            <PinChips
              pins={[finishPin]}
              onChange={() => onFinishPinChange(null)}
              colour="primary"
            />
          )}

          {/*
            The catalogue, beside the two ends as well as beside the stops.

            The pickers above want a name typed at them. Choosing where an
            operation is based, or where its days finish, is exactly when
            somebody does not have one — they are looking for a place that
            suits, and "what has already been worked near here?" is part of
            what suits.
          */}
          <Button
            size="small"
            startIcon={<TravelExploreIcon />}
            onClick={() => setBrowsing('trip')}
            sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
          >
            Browse places for the base or destination
          </Button>

          <Box>
            <Typography variant="caption" color="text.secondary">Transport</Typography>
            {/*
              Seven buttons, one row. Left to wrap, the seventh dropped to a
              line of its own and looked like a different control.
            */}
            <ToggleButtonGroup
              exclusive size="small" value={mode}
              onChange={(_, next) => next && setMode(next)}
              sx={{ display: 'flex', mt: 0.5 }}
            >
              {SELECTABLE_TRAVEL_MODES.map((m) => (
                <ToggleButton key={m} value={m} sx={{ flex: 1, minWidth: 0, px: 0.5 }}>
                  <Tooltip title={MODE_LABELS[m]} arrow>
                    <Box sx={{ display: 'flex' }}>{MODE_ICONS[m]}</Box>
                  </Tooltip>
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>

          <Stack direction="row" spacing={2}>
            <TextField
              size="small" type="time" label="Set off each day at"
              value={startTime} onChange={(e) => setStartTime(e.target.value)}
              sx={{ flex: 1 }}
            />
          </Stack>


          <Divider />

          {/*
            One explanation, not two.

            The heading carried a paragraph of its own — "An emphasis shapes
            every day — it reserves a share of each day's stops before the
            route is worked out..." — and the line above the field then said
            the same thing again in plainer words. Two captions between a
            heading and a single dropdown read as two different instructions.

            The sentence about a day taking its own is kept: it is the only
            part that was about THIS form rather than about emphases.
          */}
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              What is the operation?
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              Choose a theme to automatically set the types of sites to visit and areas
              to avoid. A day can still take its own below.
            </Typography>
          </Box>
          <Autocomplete
            size="small"
            options={themes.map((t) => t.key)}
            value={themeKey}
            onChange={(_, next) => setThemeKey(next)}
            getOptionLabel={(key) => {
              const theme = themes.find((t) => t.key === key);
              return theme ? themeName(theme.key, theme.label) : key;
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Mission emphasis"
                placeholder={themeKey ? '' : 'Pick one'}
                helperText={
                  themes.find((t) => t.key === themeKey)?.description
                  ?? undefined
                }
              />
            )}
          />

        </Stack>
      </Paper>

      {/*
        What each day's search will cover, on request.

        Diagnostics, not planning: it answers "why did this come back empty",
        which is a question somebody building a city asks and an operator
        running an operation does not. Dev only, as the single day's is.

        Offered once there is somewhere to start from — before that there is
        no corridor, and a shape drawn from half a request would be a guess
        presented as a fact.
      */}
      {FEATURES.mapDiagnostics && draft && onCoverageChange && (
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={showCoverage}
              onChange={(e) => setShowCoverage(e.target.checked)}
            />
          }
          label={
            <Typography variant="body2">
              Show the area each day will search
            </Typography>
          }
        />
      )}

      {/* ── places to visit ──────────────────────────────────────── */}
      {/*
        Folded until wanted, as on the single-day form.

        Both of these are long, and neither is needed on most operations: an
        emphasis already says what the days draw from, and plenty of taskings
        exclude nothing. Open, they pushed the dates, the hours and "Plan the
        operation" below the fold every time. A section that already holds
        something opens itself, so nothing anybody set is hidden behind a fold.
      */}
      <Accordion
        variant="outlined"
        disableGutters
        defaultExpanded={interests.length > 0 || mustVisit.length > 0}
        sx={{ bgcolor: 'transparent' }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ flex: 1 }}>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <Typography variant="caption" sx={{ flex: 1, fontWeight: 700, letterSpacing: 0.4 }}>
          SITES TO VISIT
        </Typography>
        {(interests.length + mustVisit.length) > 0 && (
          <Chip size="small" variant="outlined" label={interests.length + mustVisit.length} />
        )}
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          Held somewhere in the operation. Placed on the first planned day, or add
          them to a particular day below.
        </Typography>
        </Box>
        </AccordionSummary>
        <AccordionDetails>
        <Stack spacing={1.5}>
          {/*
            What every day draws from unless it says otherwise.

            There was deliberately no trip-level interests field, on the
            grounds that the style already says what the operation is for.
            That held while this was a filter; as a choice it is the same
            question asked once instead of on every day, and a day that wants
            different kinds still overrides it on the day.
          */}
          <SitesModeSwitch
            mode={sitesMode}
            onChange={(next) => {
              if (next === 'named' && themeKey) {
                setClearingTheme(next);

                return;
              }
              setSitesMode(next);
            }}
            scope="operation"
          />

          {/*
            How long each day has, beside the stops it is a length for, and
            only while the planner is the one choosing them. Same pairing as
            the one-day form, for the same reason.
          */}
          {sitesMode === 'kinds' && budgetDecides && (
            <DurationField
              label="Time on task each day"
              purpose={'How much total time is available for each day, including both '
                + 'travel and stops. The planner uses this to decide how many sites can '
                + 'reasonably fit. Any day can take its own.'}
              minutes={availableMinutes}
              onChange={setAvailableMinutes}
              sx={{ mb: 1 }}
            />
          )}

          <Dialog
            open={clearingTheme !== null}
            onClose={() => setClearingTheme(null)}
            maxWidth="xs"
          >
            <DialogTitle>Naming the sites yourself?</DialogTitle>
            <DialogContent>
              <Typography variant="body2">
                The emphasis is choosing sites for every day of this operation.
                Naming them yourself takes it off, so no day is given anything
                you did not ask for.
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                The kinds it filled in stay on the form, so switching back brings
                them with it.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setClearingTheme(null)}>Keep the emphasis</Button>
              <Button
                variant="contained"
                onClick={() => {
                  setThemeKey(null);
                  if (clearingTheme) setSitesMode(clearingTheme);
                  setClearingTheme(null);
                }}
              >
                Take it off and continue
              </Button>
            </DialogActions>
          </Dialog>

          {sitesMode === 'kinds' && (<>
          <CategoryChoice
            categories={visitable}
            value={interests}
            onChange={(keys) => {
              setInterests(keys);
              setCategoryDwell((current) => Object.fromEntries(
                Object.entries(current).filter(([key]) => keys.includes(key))));
            }}
            dwellMinutes={categoryDwell}
            onDwellChange={(key, minutes) => setCategoryDwell((current) => {
              const next = { ...current };
              if (minutes === null) delete next[key];
              else next[key] = minutes;
              return next;
            })}
            label="Kinds of site, every day"
            helperText={interests.length === 0
              ? 'Each day draws from its route style. A day can still name its own.'
              : 'Every day draws from these unless it names its own.'}
          />
          <BoostChoice
            categories={visitable}
            chosen={interests}
            value={boostKeys}
            onChange={setBoostKeys}
          />
          </>)}

          {/*
            Naming a place works in both modes, as on the single day: the
            planner filling the round never meant it fills it alone. Offered
            once there is a kind to add to, and forced open — with the switch
            locked — once something is actually named, so a stop that is on
            the operation always has a row to see and remove.
          */}
          {sitesMode === 'kinds' && budgetDecides && (
            <FormControlLabel
              sx={{ m: 0, mt: 0.5 }}
              control={(
                <Switch
                  size="small"
                  checked={showNaming}
                  disabled={mustVisit.length > 0}
                  onChange={(event) => setAlsoNaming(event.target.checked)}
                />
              )}
              label={<Typography variant="body2">Also visit places I name</Typography>}
            />
          )}

          {/*
            The catalogue, before anything has been named.

            The picker below answers "which place did you mean?" and needs a
            name to start from. Somebody building an operation has the other
            question first, and over several days it matters more: a week's
            work that repeats ground the team covered last month is a week
            nobody checked.
          */}
          {/*
            Not behind the naming switch.

            Browsing is how somebody DECIDES to name a place: hiding the
            catalogue until naming is switched on asked them to commit to
            the answer before they had seen the question. Taking a place
            from it turns naming on by itself, because the list it lands in
            is the one that switch reveals.
          */}
          <Button
            size="small"
            variant="outlined"
            startIcon={<TravelExploreIcon />}
            onClick={() => setBrowsing('trip')}
            sx={{ alignSelf: 'flex-start', mb: 0.5, textTransform: 'none' }}
          >
            Browse places, with their coverage
          </Button>

          {showNaming && (
          <PlacePicker
            cityId={city.id}
            allCities
            label="Which place to add?"
            placeholder={
              interests.length > 0 ? 'Search within those kinds' : 'Search any place'}
            visitableOnly
            {...(interests.length > 0 ? { categoryKeys: interests } : {})}
            value={mustVisit}
            onChange={onMustVisitChange}
            reserved={[
              ...(base ? [{ id: base.id, why: 'This is the base the days set off from' }] : []),
              ...(objective ? [{ id: objective.id, why: 'This is where the days finish' }] : []),
            ]}
          />
          )}

          {/*
            One row per chosen place, as on the single-day screen. Blank takes
            the category's own default, so this is a way of saying "except
            this one" rather than a set of fields to fill in.
          */}
          <StayRows
            places={mustVisit}
            pins={[]}
            categories={visitable}
            stopDwellMinutes={stopDwellMinutes}
            pinnedFallback={30}
            colourFor={colourFor}
            onPlaceChange={setStopDwell}
            onPinChange={() => {}}
            onRemovePlace={(poiId) => {
              setStopDwell(poiId, null);
              onMustVisitChange(mustVisit.filter((p) => p.id !== poiId));
            }}
            onShowDetails={onShowDetails}
          />

          {/*
            Shown wherever a place is being named by hand, not in the named
            mode alone. See the one-day form: "Also visit places I name"
            opens a picker that wants a place by name, and the suggestions
            are the only thing that answers which place that should be.
          */}
          {showNaming && (
          <>
          {/*
            The planner's own recommendations panel, not a second one that
            behaves nearly the same. Same interest picker, same ranking, same
            detour figures — asked here as a round trip from where you are
            staying, which IS the first day's corridor for a based trip and a
            fair approximation of the rest. A day that wants its own ranking
            has one, on the day, against its own endpoints.

            Always here and shut until asked for, as on the single day: it
            used to appear only once there was a draft to rank against, so
            opening this section to look for suggestions gave an empty box and
            no reason for it. `ready` moves that condition onto the button,
            which can say what is missing.
          */}
          <StopRecommendations
            state={recommendations}
            ready={Boolean(draft)}
            cityId={city.id}
            categories={visitable}
            chosenIds={mustVisit.map((p) => p.id)}
            onAddStop={(poi) => {
              if (!mustVisit.some((p) => p.id === poi.id)) {
                onMustVisitChange([...mustVisit, poi]);
              }
            }}
            onShowDetails={onShowDetails}
          />
          </>
          )}

        </Stack>
        </AccordionDetails>
      </Accordion>

      {/* ── places to avoid ──────────────────────────────────────── */}
      <Accordion
        variant="outlined"
        disableGutters
        defaultExpanded={avoidCategories.length > 0 || avoid.length > 0}
        sx={{ borderColor: 'warning.light', bgcolor: 'transparent' }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ flex: 1 }}>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <Typography variant="caption" sx={{ flex: 1, fontWeight: 700, letterSpacing: 0.4 }}>
                EXCLUSION ZONES
              </Typography>
              {(avoidCategories.length + avoid.length) > 0 && (
                <Chip
                  size="small"
                  variant="outlined"
                  label={avoidCategories.length + avoid.length}
                />
              )}
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              Held clear on every day of the operation.
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>

        <Stack spacing={1.5}>
          <AvoidancePanel
            cityId={city.id}
            onShowDetails={onShowDetails}
            categories={categories}
            {...(avoidPoints?.length && onAvoidPointsChange
              ? {
                  // Areas marked on the map, listed with the places avoided
                  // by name so the section says everything being kept clear of.
                  pinRow: (
                    <PinChips
                      pins={avoidPoints}
                      onChange={onAvoidPointsChange}
                      colour="warning"
                    />
                  ),
                }
              : {})}
            filter={avoidFilter}
            onFilterChange={setAvoidFilter}
            avoid={avoid}
            onAvoidChange={onAvoidChange}
            avoidCategories={avoidCategories}
            onAvoidCategoriesChange={onAvoidCategoriesChange}
            avoidDistanceM={avoidDistanceM}
            onAvoidDistanceChange={onAvoidDistanceChange}
            colourFor={colourFor}
            avoidDistanceByPoiId={avoidDistanceByPoiId}
            avoidDistanceByCategoryKey={avoidDistanceByCategoryKey}
            onAvoidDistanceOverride={onAvoidDistanceOverride}
          />
        </Stack>
        </AccordionDetails>
      </Accordion>

      {/* ── breaks and preferences ───────────────────────────────── */}
      {/*
        Collapsed, as on the single-day screen. Most trips take the defaults,
        and four controls nobody is looking at push the days themselves —
        which is what a trip is about — below the fold.
      */}
      <Accordion variant="outlined" disableGutters>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flex: 1 }}>
            <Typography variant="body2" sx={{ flex: 1 }}>Breaks and preferences</Typography>
            {(Number(lunch) + Number(coffee)) > 0 && (
              <Chip size="small" variant="outlined" label={Number(lunch) + Number(coffee)} />
            )}
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          On every day unless a day says otherwise. Name a place and it is used
          each time; leave it empty and one is chosen near where you will be.
        </Typography>

        <Stack spacing={1.5}>
          {/*
            The single-day screen's own break rows, not a second pair that
            behave nearly the same. Same layout, same labels, same ranked
            suggestions — a trip is a list of days, so the control for "eat
            around one" should not be a different control here.
          */}
          <BreakRow
            label="Meal stop"
            enabled={lunch}
            onEnabledChange={setLunch}
            at={lunchAt}
            onAtChange={setLunchAt}
            minutes={lunchMinutes}
            onMinutesChange={setLunchMinutes}
            cityId={city.id}
            categoryKeys={BREAK_CATEGORIES.lunch}
            place={lunchPlace}
            onPlaceChange={setLunchPlace}
            role="lunch"
            pinned={lunchPinned}
            onPinnedChange={setLunchPinned}
            mode={breakMode.lunch}
            onModeChange={(next) => setBreakMode((c) => ({ ...c, lunch: next }))}
            recommendations={recommendations}
            canSuggest={Boolean(draft)}
            onShowDetails={onShowDetails}
            placeholder="Leave empty and one is chosen each day"
          />

          <BreakRow
            label="Rest stop"
            enabled={coffee}
            onEnabledChange={setCoffee}
            at={coffeeAt}
            onAtChange={setCoffeeAt}
            minutes={coffeeMinutes}
            onMinutesChange={setCoffeeMinutes}
            cityId={city.id}
            categoryKeys={BREAK_CATEGORIES.coffee}
            place={coffeePlace}
            onPlaceChange={setCoffeePlace}
            role="coffee"
            pinned={coffeePinned}
            onPinnedChange={setCoffeePinned}
            mode={breakMode.coffee}
            onModeChange={(next) => setBreakMode((c) => ({ ...c, coffee: next }))}
            recommendations={recommendations}
            canSuggest={Boolean(draft)}
            onShowDetails={onShowDetails}
            placeholder="Leave empty and one is chosen each day"
          />


          {/*
            The two the single-day screen keeps in this same section, in the
            same order. They were lost from the trip form when the road
            questions moved out to a section of their own — they had been
            sitting among them, which is not where the other screen puts
            them and not what they are about.
          */}
          {/*
            Tolls are answered in "How this operation travels", not here —
            removed for the same reason as on the single-day form, so the two
            screens do not offer the same road two different ways.
          */}

          {/*
            Whether the proximity score is shown is an Admin setting, not a
            per-operation one — removed here for the same reason as on the
            single-day form, so the two screens do not disagree about whether
            it is a choice.
          */}
        </Stack>
        </AccordionDetails>
      </Accordion>

      {/*
        How this operation travels.

        Its own section, as on the single-day screen, and for the reason it
        was moved: these sat inside "Breaks and preferences", which is not
        somewhere anybody looks for the kind of road a week takes. The name
        of the section IS the question, so it has to be readable without
        opening anything.

        Shut by default. An emphasis answers all of it, and most operations
        want whatever it says — this is for the one that wants something
        else.
      */}
      <Accordion variant="outlined" disableGutters>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flex: 1 }}>
            <Typography variant="body2" sx={{ flex: 1 }}>
              {chosenThemeForRoads
                ? `How this operation travels — ${chosenThemeForRoads.label} decides`
                : 'How this operation travels'}
            </Typography>
            {roadAnswerCount > 0 && (
              <Chip size="small" variant="outlined" label={roadAnswerCount} />
            )}
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          <JourneyCharacterFields
            theme={chosenThemeForRoads}
            roadOptions={roadOptions}
            onRoadOptionsChange={setRoadOptions}
            characteristics={characteristics}
            onCharacteristicsChange={setCharacteristics}
            appliesTo="every day of this operation"
            // As on the day form: the strong form of an "away" wish, offered
            // beside the weak one. Here it holds for every day.
            avoidCategories={avoidCategories}
            onAvoidCategoriesChange={onAvoidCategoriesChange}
          />
        </AccordionDetails>
      </Accordion>

      {/* ── the days ─────────────────────────────────────────────── */}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" sx={{ alignItems: 'center', mb: 0.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.4, flex: 1 }}>
            THE DAYS
          </Typography>
          <Chip size="small" variant="outlined" label={`${active.length} planned`} />
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          Dates follow the operation’s start. Open a day to change where it goes, how
          you travel, what it is about, or to pick places from what is nearby.
        </Typography>

        <Stack spacing={1}>
          {days.map((day, index) => (
            <TripDayCard
              key={index}
              index={index}
              day={day}
              cityId={city.id}
              styles={styles}
              categories={categories}
              removable={days.length > 1}
              tripStyleKeys={styleKeys}
              tripInterests={interests}
              {...(dayStopPins?.[index] ? { markedStops: dayStopPins[index] } : {})}
              {...(dayEndPins?.[index] ? { markedEnds: dayEndPins[index] } : {})}
              {...(onDayEndPinChange
                ? {
                    onMarkedEndChange: (role: 'start' | 'end', pin: CoordStop | null) =>
                      onDayEndPinChange(index, role, pin),
                  }
                : {})}
              {...(onDayStopPinsChange
                ? {
                    onMarkedStopsChange: (points: CoordStop[]) =>
                      onDayStopPinsChange(index, points),
                  }
                : {})}
              tripLunch={lunch}
              tripCoffee={coffee}
              tripMode={mode}
              tripStartTime={startTime}
              tripAvailableMinutes={availableMinutes}
              tripBase={base}
              tripFinish={objective}
              tripAvoidCount={avoid.length + avoidCategories.length}
              colourFor={colourFor}
              onShowDetails={onShowDetails}
              {...(onSuggestionPreview ? { onSuggestionPreview } : {})}
              previousEnd={index > 0
                ? (days[index - 1]?.end ?? days[index - 1]?.start ?? objective ?? null)
                : null}
              onBrowsePlaces={() => setBrowsing(index)}
              onChange={(patch) => setDay(index, patch)}
              onRemove={() => setDays((c) => c.filter((_, i) => i !== index))}
            />
          ))}
        </Stack>

        <Button
          size="small" startIcon={<AddIcon />}
          disabled={days.length >= maxDays}
          onClick={() => setDays((c) => [...c, blankDay(addDays(tripStart, c.length))])}
          sx={{ mt: 1.5 }}
        >
          Add a day
        </Button>
      </Paper>

      {!hasDirection && (
        <Alert severity="info" variant="outlined">
          Choose a route style for the trip, or give every day one of its own.
          Without either, each day would come back as the drive from your hotel
          and straight back again.
        </Alert>
      )}

      {/* Above the button, so it is read before it is pressed. */}
      {placeProblems.length > 0 && (
        <Alert severity="warning" variant="outlined">
          {placeProblems.map((problem) => (
            <Typography key={problem} variant="body2">{problem}</Typography>
          ))}
        </Alert>
      )}

      <Button
        variant="contained" size="large"
        disabled={!ready || planning || placeProblems.length > 0}
        onClick={submit}
        startIcon={planning ? <CircularProgress size={16} color="inherit" /> : undefined}
      >
        {planning ? `Planning ${active.length} days…` : `Plan ${active.length} days`}
      </Button>

      {/*
        The catalogue, filling whichever list asked for it.

        One window for the operation and for every day, because it is one
        act — read what has been worked here, then say what this place is
        to be. What it writes to is the only difference, and that is what
        `browsing` carries. Mounted only while open: it holds a search, a
        filter and a coverage fetch.
      */}
      {browsing !== null && (
        <PlaceChooserDialog
          open
          city={city}
          categories={categories}
          {...(basemapUrl ? { basemapUrl } : {})}
          onClose={() => setBrowsing(null)}
          onSetStart={(poi) => {
            if (browsing === 'trip') onBaseChange(poi);
            else setDay(browsing, { start: poi });
          }}
          onSetEnd={(poi) => {
            if (browsing !== 'trip') {
              setDay(browsing, { end: poi });

              return;
            }
            /*
              And the switch that hides the field goes with it: an
              operation set to return to its base has nowhere to put a
              destination, so choosing one while it is on would do nothing
              anybody could see.
            */
            setReturnToBase(false);
            onFinishChange(poi);
          }}
          onAddStop={(poi) => {
            if (browsing === 'trip') {
              if (!mustVisit.some((p) => p.id === poi.id)) {
                onMustVisitChange([...mustVisit, poi]);
              }

              return;
            }
            const day = days[browsing];
            if (day && !day.mustVisit.some((p) => p.id === poi.id)) {
              setDay(browsing, { mustVisit: [...day.mustVisit, poi] });
            }
          }}
          inRound={browsing === 'trip'
            ? mustVisit.map((p) => p.id)
            : (days[browsing]?.mustVisit ?? []).map((p) => p.id)}
          {...(browsing === 'trip'
            ? (base ? { startId: base.id } : {})
            : (days[browsing]?.start ?? base) ? { startId: (days[browsing]?.start ?? base)?.id } : {})}
          {...(browsing === 'trip'
            ? (objective ? { endId: objective.id } : {})
            : (days[browsing]?.end ?? objective) ? { endId: (days[browsing]?.end ?? objective)?.id } : {})}
        />
      )}
    </Stack>
  );
}
